/**
 * The sidebar's Analytics section — **client review R7**.
 *
 * > *"The space freed at the bottom of the left sidebar becomes an Analytics section for the
 * > current indicator+scope (big value, ▲/▼ delta, spark, mini bars — reuse the
 * > IndicatorPopover content/components) … Updates live with year/mode/scope/country."*
 *
 * Its subject is `state.indicator` — **what the map is painting** — and `state.scope`, so it
 * answers the question a choropleth raises and does not answer: *what is the number?* The
 * §F5.9 popover asked the reader to know that six round buttons on the right edge of the map
 * held it; this asks nothing, because it is already open, three centimetres under the row
 * they clicked to map the indicator in the first place.
 *
 * "Updates live" needs no code: every figure inside `IndicatorAnalytics` hangs off a §F1
 * query key carrying the country, the scope, the year and the cumulative mode, so a time-bar
 * tick, a cascade pick or a country change re-reads through the TanStack cache.
 *
 * **Nothing mapped is a real state**, and it is the one this component has to render itself:
 * §F5.2's row click is a *toggle*, so a reader can and does unmap the indicator, and an empty
 * section with no explanation reads as a section that failed to load. The `+` overlays and
 * the bivariate Y are deliberately not described here — the section is about the fill, which
 * is the layer the legend, the tooltip and the unit profile are also about.
 */
import { useTranslation } from 'react-i18next';

import { useAppStore } from '../../lib/state';
import { IndicatorAnalytics } from './IndicatorAnalytics';
import { useIndicatorMeta } from './indicatorMeta';

/** `#sidebar` is 288 px since S3's 5/20/75 layout; its padding leaves this for a chart. */
const SPARK_WIDTH = 250;

export function SidebarAnalytics() {
  const { t } = useTranslation();
  const indicator = useAppStore((s) => s.indicator);
  const sidebarOpen = useAppStore((s) => s.sidebarOpen);
  const { ind, color } = useIndicatorMeta(indicator);

  return (
    <section id="sbAnalytics" aria-label={t('analytics.title')}>
      <div className="sb-title sba-head">
        <span className="sba-title-text">
          {ind ? (
            <span
              className="dot"
              data-sbadot=""
              style={{ background: color }}
              aria-hidden="true"
            />
          ) : null}
          {t('analytics.title')}
        </span>
      </div>
      {indicator && ind ? (
        <>
          <div className="sba-ind" data-sbaind={ind.id} title={ind.full_name ?? ind.name}>
            {ind.name}
          </div>
          {/*
            No "Map this indicator" button: this section's subject *is* the mapped indicator,
            so the control would map what is already mapped. "Dashboard" stays — it is the
            one place a reader goes from a number to its whole story (§F7).
          */}
          {/* `visible`: the sidebar collapses by class rather than by unmounting, and the
              sibling fan-out is twenty requests a change — see `SiblingBars`. */}
          <IndicatorAnalytics
            indicatorId={indicator}
            width={SPARK_WIDTH}
            showMapButton={false}
            visible={sidebarOpen}
          />
        </>
      ) : (
        <div className="sba-empty" data-sbaempty="">
          {t('analytics.noIndicator')}
        </div>
      )}
    </section>
  );
}

export default SidebarAnalytics;
